/**
 * `How to use` — a small button beside a page's title that opens a right-hand drawer
 * carrying a short numbered how-to for that screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS REPLACED THE ALWAYS-OPEN STRIP
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every screen used to carry an `About This Screen` paragraph under its title, open on every
 * visit, plus a grey subtitle beside the title saying much the same thing again. The
 * Technical Delegate tested that build on 20 August 2026 and reported the opposite defect to
 * the one it fixed: *"very very wordy — text everywhere, hard to concentrate."* He was right.
 * Two blocks of prose above the first control on thirteen screens is a wall the eye has to
 * get past before it can reach the work, and it is there on the hundredth visit as loudly as
 * on the first.
 *
 * The guidance itself was not the problem — the permanence was. So it moves behind one small
 * control, in the same place on every screen, and the page underneath it is quiet.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE DRAWER SAYS, AND HOW
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  · **Numbered, three to seven steps.** A how-to, not a description. Each step is one thing
 *    the reader does.
 *  · **In the order the demonstration walks the page**, so the drawer doubles as the script.
 *  · **Controls are named by their LABEL, never by their position** — `the Match Day
 *    dropdown, top right`, never `the dropdown above`. A layout that moves must not turn the
 *    instructions into lies.
 *  · **A step that names a control links to it.** Pressing the link closes the drawer, scrolls
 *    the page to that control and pulses it for a moment, so the reader's eye lands on the
 *    thing the sentence was about rather than hunting for it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT NEVER OPENS BY ITSELF
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Not on a first visit, not on a hash, not on anything. A panel that appears over a screen a
 * Technical Delegate opened to do work is the wordiness problem wearing a different coat.
 * The button remembers, per screen, that it has been read, and takes a quieter style once it
 * has — which changes nothing about what the page does.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 31 — WHY THE DRAWER IS A PORTAL, AND WHY A BIGGER `z-index` WAS NOT THE FIX
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Technical Delegate sent a screenshot of the Command Desk with the drawer open and the
 * `Appointment Status` and `Alerts` panels of the rail painted straight over the top of it.
 * The drawer already carried `z-index: 100`, higher than every panel on the page, and the
 * panels have no `z-index` at all — so the number was never the problem.
 *
 * **The cause was the ancestor, not the drawer.** The button and the drawer were rendered as
 * siblings, wherever the page put `<HowToUse>` — inside `.dateline` on the Command Desk and
 * inside `.fixband` on Fixtures. Both of those carry `animation: fadeup … both`, and `fadeup`
 * animates `transform` and `opacity`. `animation-fill-mode: both` means the animation keeps
 * applying **for ever** after it has finished, and an element with a filling animation of a
 * stacking-context property IS a stacking context for as long as it applies. So `.dateline`
 * became a permanent stacking context at the root's level 0, the drawer's `z-index: 100` was
 * scoped INSIDE it and could not be seen from outside, and `.panel` — which carries the same
 * `fadeup … both` and is therefore its own level-0 stacking context — simply came later in
 * document order and won. That is exactly the screenshot: a drawer that is drawn, is
 * full-height, takes its clicks, and has panels lying on top of it.
 *
 * `position: fixed` still resolved against the viewport, because `fadeup` ends on
 * `transform: none` — which is why the drawer was in the right PLACE and the wrong LAYER, and
 * why the defect looked like a paint bug rather than a layout one.
 *
 * **The fix is a portal to `document.body`.** The scrim and the drawer leave the page's
 * subtree entirely, so no ancestor of theirs can create a stacking context around them: they
 * are direct children of `<body>` and compete in the ROOT stacking context, where the raised
 * `z-index` in the stylesheet is finally a number that means something. Raising the number
 * alone would have fixed nothing — a `z-index` cannot escape a trapping ancestor — and
 * un-animating `.dateline`, `.fixband` and `.panel` would have fixed this page and left the
 * next one that grows an animation, a `transform` or a `filter` broken again. The portal is
 * the only fix that is immune to whatever a page does above the button.
 *
 * The trigger BUTTON deliberately stays where it is: it belongs to the page's title band, must
 * flow with it, and is not part of the overlay.
 *
 * React's event system still bubbles through the React tree rather than the DOM tree, so the
 * focus trap, `Escape`, the scrim's click-to-close and the jump links all work unchanged.
 */

import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** `localStorage` key for one screen's read mark. Per screen, so a new page is new to it. */
function readKey(id: string): string {
  return `hockeytd.howto.read.${id}`;
}

function hasBeenRead(id: string): boolean {
  try {
    return window.localStorage.getItem(readKey(id)) === "1";
  } catch {
    // Private-mode Safari throws on `localStorage` outright. A drawer that cannot remember is
    // a drawer that always looks unread, which is the harmless half of the failure.
    return false;
  }
}

function markRead(id: string): void {
  try {
    window.localStorage.setItem(readKey(id), "1");
  } catch {
    // See `hasBeenRead` — nothing on this screen depends on the mark.
  }
}

/** How the drawer asks the page to go somewhere: close, scroll, pulse. */
type JumpFn = (anchor: string) => void;

const HowToJumpContext = createContext<JumpFn | null>(null);

/**
 * A step's reference to a control or a panel on the page behind the drawer.
 *
 * `to` names a `data-howto` attribute placed on the real element — a panel's section, a
 * button, a table. The element is the anchor, so the link cannot point at somewhere the
 * control merely used to be.
 */
export function HowToLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const jump = useContext(HowToJumpContext);
  return (
    <button
      type="button"
      className="howto__jump"
      data-howto-to={to}
      onClick={() => {
        jump?.(to);
      }}
    >
      {children}
    </button>
  );
}

/**
 * How long the target stays lit after a jump.
 *
 * F-21 — the Technical Delegate, 20 August 2026: the landing highlight *"is not obvious —
 * maybe a different colour line surrounding the section or table briefly"*. It was a 1.5
 * second wash of the accent tint, which on a panel the size of the Fixture List is a change
 * of shade the eye can miss entirely. It is now a 3px accent ring drawn right round the
 * target, over a light tint, fading across 2.5 seconds — long enough to find on a full page,
 * and gone before it becomes furniture. The ring is a `box-shadow`, so it shifts nothing.
 */
const PULSE_MS = 2500;

/**
 * Scroll to an element and pulse it — the house landing, in one place.
 *
 * The class is removed and re-added around a forced reflow so a second press on the same link
 * flashes again rather than doing nothing, and the timer that clears it is per element.
 *
 * `block: "center"` puts the target in the middle of the screen rather than under the top
 * edge, and it does so in **every** scrolling ancestor — which is what makes this work on the
 * TO Appointments screen, where the Fixture List is a pane that scrolls inside itself: a row
 * or a panel inside that pane is centred in the pane and the pane is centred in the page, in
 * one call.
 *
 * **Run 78 exported it.** The capture screen now lands on the one note a `#note-<id>` hash
 * names, arriving from `Open In Match ›` on My Notes, and F-21's ring is exactly the landing
 * that was already tuned for a Technical Delegate who could not see the old tint. A second
 * copy would be a second thing to re-tune. Nothing about the drawer's own behaviour changed:
 * `jumpToAnchor` still finds its `data-howto` target and still calls precisely these lines.
 *
 * `scrollIntoView` is called only when it exists. jsdom has no layout and does not implement
 * it, and a landing that threw on the way to the highlight would take the highlight with it —
 * the same guard `AppointmentsPanel` puts on its own call, for the same reason.
 */
export function pulseElement(target: HTMLElement): void {
  if (typeof target.scrollIntoView === "function") {
    target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
  }
  target.classList.remove("howto-pulse");
  void target.offsetWidth;
  target.classList.add("howto-pulse");
  window.setTimeout(() => {
    target.classList.remove("howto-pulse");
  }, PULSE_MS);
}

function jumpToAnchor(anchor: string): void {
  const target = document.querySelector(`[data-howto="${anchor}"]`);
  if (!(target instanceof HTMLElement)) return;
  pulseElement(target);
}

/** Everything inside the drawer that can take focus, in document order. */
function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ),
  );
}

/**
 * Where the UM Onboarding Guide is served from — the same PDF handed to the managers at
 * onboarding, hosted on this origin (`apps/web/public/guide/`, copied into the bundle by
 * Vite). The Technical Delegate's ruling of 2026-09-11 (run 76): every UM `How to use`
 * drawer carries a quiet link to it at the foot, so a manager who wants the whole story
 * rather than one screen's steps can reach it from anywhere in the app.
 *
 * It appears on UM screens ONLY — decided by the `um-` id prefix every UM screen already
 * uses — because the guide is the managers' document: a Technical Delegate reading the
 * Fixtures how-to would be offered a manual about somebody else's app.
 */
export const UM_GUIDE_PATH = "/guide/UM-Onboarding-Guide.pdf";

export interface HowToUseProps {
  /** Stable, per screen — `appointments`, `hearings`, `um-today`. Keys the read mark. */
  id: string;
  /** The drawer's own heading, naming the screen: `How to use TO Appointments`. */
  title: string;
  /** The steps, as `<li>` elements. The component supplies the numbered list around them. */
  children: React.ReactNode;
}

export function HowToUse({ id, title, children }: HowToUseProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  /**
   * `open` is the state; `sliding` is the class that drives the transform.
   *
   * The drawer is **mounted only while it is open**, which is a correctness point rather than
   * a nicety: its steps quote the page's own control labels back at the reader, so a drawer
   * that sat in the DOM permanently would put a second `Evidence — All Tournament` — and a
   * second of every other label — on every screen, where anything reading the page by its text
   * would find the copy in the drawer instead of the control itself. Mounted-when-open means
   * the page reads exactly as it looks.
   *
   * The one frame between mounting and adding the open class is what makes the slide a slide:
   * a node that mounts already translated to zero has nothing to animate from.
   */
  const [sliding, setSliding] = useState(false);
  const [read, setRead] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();

  // Read once on mount rather than on every render: `localStorage` is synchronous and this
  // sits in the masthead band of every screen.
  useEffect(() => {
    setRead(hasBeenRead(id));
  }, [id]);

  const close = useCallback(() => {
    setSliding(false);
    setOpen(false);
  }, []);

  const openDrawer = useCallback(() => {
    setOpen(true);
    markRead(id);
    setRead(true);
  }, [id]);

  const jump = useCallback<JumpFn>((anchor) => {
    setSliding(false);
    setOpen(false);
    // One frame after the close, so the drawer is out of the way before the page scrolls and
    // the reader sees the movement rather than arriving after it.
    window.setTimeout(() => {
      jumpToAnchor(anchor);
    }, 60);
  }, []);

  // Body scroll lock, focus into the drawer, focus back to the button on close, Escape, and
  // the Tab cycle — all of it only while the drawer is open, and all of it undone on close.
  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      setSliding(true);
    });

    const drawer = drawerRef.current;
    const first = drawer === null ? [] : focusables(drawer);
    first[0]?.focus({ preventScroll: true });

    function onKeyDown(event: KeyboardEvent): void {
      // RUN 82K (lock layer QA pass 5) — a modal dialogue the page opened over the drawer (a TO
      // Appointments question raised by a refusal while the drawer was open) owns the keyboard:
      // the drawer's Escape used to close the drawer and put the focus on How To Use, behind the
      // question, and its Tab cycle pulled the focus out of the question. RUN 82M (QA pass 6) —
      // wherever the focus is, unless it is in the drawer itself: a click on the page behind the
      // question leaves the focus on the page body, and Escape from there still closed the drawer.
      const focused = document.activeElement;
      const modalOutsideDrawer =
        drawer !== null &&
        [...document.querySelectorAll('[aria-modal="true"]')].some(
          (element) => !drawer.contains(element) && !element.contains(drawer),
        );
      if (modalOutsideDrawer && (focused === null || drawer === null || !drawer.contains(focused))) {
        return;
      }
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        return;
      }
      if (event.key !== "Tab" || drawer === null) return;
      const items = focusables(drawer);
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (firstItem === undefined || lastItem === undefined) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === firstItem || !drawer.contains(active))) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && active === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      triggerRef.current?.focus({ preventScroll: true });
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={read ? "howto-cta howto-cta--read" : "howto-cta"}
        data-testid={`howto-cta-${id}`}
        aria-expanded={open}
        onClick={openDrawer}
      >
        How to use
      </button>

      {/*
        RUN 31 — the scrim and the drawer are portalled to `document.body`. See the header:
        rendered here they were trapped inside whichever animated band the page put the button
        in, and the desk's panels painted over them. `document.body` is read at render time
        rather than cached, because a test environment tears the body down between cases.
      */}
      {!open
        ? null
        : createPortal(
            <div className="howto-layer" data-testid={`howto-layer-${id}`}>
              <div
                className={sliding ? "howto-overlay howto-overlay--open" : "howto-overlay"}
                data-testid={`howto-overlay-${id}`}
                aria-hidden="true"
                onClick={close}
              />

              <div
                className={sliding ? "howto-drawer howto-drawer--open" : "howto-drawer"}
                data-testid={`howto-drawer-${id}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                ref={drawerRef}
              >
                <div className="howto-head">
                  <h2 id={titleId}>{title}</h2>
                  <button
                    type="button"
                    className="howto-close"
                    aria-label="Close how to use"
                    onClick={close}
                  >
                    <span aria-hidden="true">✕</span>
                  </button>
                </div>

                <div className="howto-body">
                  <HowToJumpContext.Provider value={jump}>
                    <ol className="howto-steps">{children}</ol>
                  </HowToJumpContext.Provider>
                  {/* UM screens only — see UM_GUIDE_PATH above. `target="_blank"` because the
                      drawer sits over a screen mid-task, and a PDF that replaced the app would
                      cost a phone its scroll position and an offline queue its page. */}
                  {id.startsWith("um-") ? (
                    <p className="howto-guide" data-testid={`howto-guide-${id}`}>
                      New here, or want the whole story?{" "}
                      <a href={UM_GUIDE_PATH} target="_blank" rel="noreferrer">
                        Open The UM User Guide
                      </a>
                    </p>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body,
          )}
    </>
  );
}
